/* eslint-disable jsx-a11y/anchor-is-valid */
import { isValidCPF, isValidEmail } from '@brazilian-utils/brazilian-utils';
import { Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { Layout } from '../../layout';

import { useQuery } from '../../hooks/useQuery';
import { isValidAlphanumericCNPJ } from '../../utils/isValidAlphanumericCNPJ';
import { login } from '../../services/toro-oidc';

export function Login() {
    const { op_it, _d: details } = useQuery({ op_it: '', _d: {} });

    const [currentToroSession, setCurrentToroSession] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [redirectUrl, setRedicrectUrl] = useState('');
    const redirectForm = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (redirectUrl && redirectForm.current) {
            redirectForm.current.submit();
        }
    }, [redirectUrl, redirectForm]);

    const navigate = useNavigate();
    const location = useLocation();
    const termLink = details?.client?.term_link;
    const pageTitle = 'Use seus dados da Toro para acessar a plataforma.';

    const validateInformationReturn = () => {
        if (details.params?.scope?.includes('cpfCnpj')) {
            return 'CPF, nome, e-mail, data de nascimento, gênero, endereço e conta na corretora';
        } else {
            return 'nome, e-mail, data de nascimento, gênero, endereço e conta na corretora';
        }
    };

    return (
        <Layout uid={op_it} title={pageTitle} message={errorMessage}>
            <Formik
                initialValues={{ username: '', credential: '' }}
                validationSchema={yup.object().shape({
                    username: yup
                        .string()
                        .test('is-username', 'Email ou CPF inválidos', (username = '') => {
                            return isValidCPF(username) || isValidEmail(username) || isValidAlphanumericCNPJ(username);
                        })
                        .required('Email ou CPF inválidos'),
                    credential: yup.string().required('Senha inválida')
                })}
                onSubmit={async (values, { setSubmitting }) => {
                    const { username, credential: password } = values;
                    try {
                        const response = await login(op_it, username, password);

                        setErrorMessage('');

                        if (response?.redirected) {
                            setRedicrectUrl(response?.location);
                        }
                    } catch (error: any) {
                        if (error.isAxiosError) {
                            const toroSession = error.response?.data?.toroSession;

                            switch (error.response?.data?.errorCode) {
                                case 'MFA_NEEDED':
                                    navigate(
                                        {
                                            pathname: '/login/mfa',
                                            search: location.search
                                        },
                                        {
                                            state: {
                                                username,
                                                password,
                                                lastFour: error.response?.data?.lastFourDigitsPhone,
                                                hasDevice: error.response?.data?.hasDevice,
                                                toroSession: currentToroSession || toroSession
                                            }
                                        }
                                    );
                                    break;
                                case 'INVALID_CREDENTIALS':
                                    setErrorMessage(error.response?.data.message);
                                    break;
                                default:
                                    const errorCode = error.response?.data.errorCode || 'UNEXPECTED';
                                    const searchParams = new URLSearchParams({
                                        op_it: op_it,
                                        errorCode: errorCode
                                    });

                                    navigate({
                                        pathname: '/login/error',
                                        search: searchParams.toString()
                                    });
                                    break;
                            }
                        }
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, touched, handleSubmit, handleChange, handleBlur, isSubmitting }) => {
                    const showUsernameError = errors.username && touched.username;
                    const showPasswordError = errors.credential && touched.credential;

                    return (
                        <form autoComplete="off" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <div className={`input-field ${showUsernameError ? 'input-error' : ''}`}>
                                    <label htmlFor="username">E-mail ou CPF</label>
                                    <input
                                        required
                                        type="text"
                                        name="username"
                                        className="form-input form-field--js "
                                        autoCapitalize="none"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.username}
                                        disabled={isSubmitting}
                                    />
                                    {showUsernameError && (
                                        <span className="input-error-message">{errors.username}</span>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <div className={`input-field ${showPasswordError ? 'input-error' : ''}`}>
                                    <label htmlFor="password">Senha</label>
                                    <input
                                        required
                                        type="password"
                                        name="credential"
                                        className="form-input form-field--js"
                                        autoCapitalize="none"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.credential}
                                        disabled={isSubmitting}
                                    />

                                    {showPasswordError && (
                                        <span className="input-error-message">{errors.credential}</span>
                                    )}
                                </div>
                            </div>

                            <div className="mt-1 mb-4">
                                <a
                                    className="font-text--medium link link-primary"
                                    href="https://app.toroinvestimentos.com.br/esqueci-minha-senha"
                                >
                                    Esqueceu a senha?
                                </a>
                            </div>
                            <p>
                                Para continuar, a Toro vai compartilhar com a plataforma o seu{' '}
                                {validateInformationReturn()}.
                                <span style={{ display: termLink ? 'inline' : 'none' }}>
                                    {' Veja o '}
                                    <a
                                        className="font-text--medium link link-primary"
                                        href=""
                                        onClick={() => navigate('/login/term', { state: { termLink } })}
                                    >
                                        Termo de Contratação
                                    </a>
                                    .
                                </span>
                            </p>

                            <button
                                type="submit"
                                className="btn btn-primary btn--md btn--full btn--solid btn-primary--solid"
                                disabled={isSubmitting}
                            >
                                Autorizar e entrar
                            </button>

                            <div className="mt-4">
                                Ainda não tem conta na Toro?{' '}
                                <a
                                    className="font-text--medium link link-primary"
                                    href="https://cadastro.toroinvestimentos.com.br/"
                                >
                                    Cadastre-se grátis.
                                </a>
                            </div>
                        </form>
                    );
                }}
            </Formik>
            <form ref={redirectForm} action={redirectUrl} method="get"></form>
        </Layout>
    );
}
